import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DownloadStatus } from '../../types/download-button-types';
import { PhotoSelectionStore } from '../../stores/photo-selection-store';
import JSZip from 'jszip';

/** A bar that appears above the photo gallery when one or more photos are selected, allowing the user to download the selected photos */
@Component({
    selector: 'photo-selection-bar',
    templateUrl: './photo-selection-bar.component.html',
    styleUrls: ['./photo-selection-bar.component.scss'],
    standalone: false
})
export class PhotoSelectionBarComponent {
  private _status: DownloadStatus = DownloadStatus.Idle;

  /**
   * @deprecated US612165: Pass-through status kept for backward compatibility with the legacy UI.
   * In the refreshed UI this component self-manages download status internally.
   * Remove once the legacy [status] binding in service-details-calls is cleaned up.
   */
  @Input()
  set status(val: DownloadStatus) {
    this._status = val;
  }
  get status(): DownloadStatus {
    return this._status;
  }

  /**
   * @deprecated US612165: Output kept for backward compatibility with the legacy UI.
   * In the refreshed UI this component handles downloads internally.
   * Remove once the legacy (download) binding in service-details-calls is cleaned up.
   */
  @Output() download = new EventEmitter<void>();

  constructor(
    private _photoSelectionStore: PhotoSelectionStore,
  ) {}

  /** Number of currently selected photos */
  get selectedCount(): number {
    return this._photoSelectionStore.count();
  }

  /** Whether the download button should be disabled */
  get disabled(): boolean {
    return this.selectedCount === 0;
  }

  /** Labels for each download status */
  get labels(): Record<DownloadStatus, string> {
    return {
      [DownloadStatus.Idle]: 'Download',
      [DownloadStatus.Running]: 'Downloading...',
      [DownloadStatus.Success]: 'Downloaded',
      [DownloadStatus.Error]: 'Retry Download',
    };
  }

  /**
   * If a legacy (download) listener is bound (service-details-calls legacy UI), delegate to it.
   * Otherwise handle the download internally (refreshed UI — no bindings on the element).
   */
  async onDownload(): Promise<void> {
    if (this.download.observed) {
      this.download.emit();
    } else {
      await this.handleDownloadSelectedPhotos();
    }
  }

  /** Emits when all photo selections are cleared */
  @Output() cleared = new EventEmitter<void>();

  /** Clear all photo selections */
  clearAllSelections(): void {
    this._photoSelectionStore.clear();
    this.cleared.emit();
  }

  private async handleDownloadSelectedPhotos(): Promise<void> {
    this._status = DownloadStatus.Running;
    await this.downloadImagesAsZip();
    this._status = DownloadStatus.Success;
    setTimeout(() => {
      this._status = DownloadStatus.Idle;
      this._photoSelectionStore.clear();
    }, 2000);
  }

  private async downloadImagesAsZip(): Promise<void> {
    const sanitizeFileName = (name: string) => name.replace(/[:]+/g, '_');

    const fileName = prompt(
      'Please enter the zip filename for the download:',
      'photo-observations',
    );
    if (fileName !== null) {
      const sanitizedFileName = sanitizeFileName(fileName);
      const zip = new JSZip();
      const folder = zip.folder(sanitizedFileName);

      const selectedPhotos = this._photoSelectionStore.getAll();

      for (const [index, photo] of selectedPhotos.entries()) {
        const response = await fetch(photo.photoUrl);
        const blob = await response.blob();
        if (folder !== null) {
          folder.file(`${sanitizedFileName}-${index}.jpg`, blob, {
            binary: true,
          });
        }
      }
      const content = await zip.generateAsync({ type: 'blob' });
      const tempLink = document.createElement('a');
      tempLink.href = URL.createObjectURL(content);
      tempLink.download = `${sanitizedFileName}.zip`;
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
    }
  }
}
